import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Panel } from "../components/layout/Panel";
import { PageErrorState } from "../components/pages/PageErrorState";
import { PageLoadingState } from "../components/pages/PageLoadingState";
import { JsonTree } from "../components/common/JsonTree";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { useLog } from "../hooks/useLog";
import { getSeverityChipColor } from "../utils/colors";
import { SpanIdLink, TraceIdLink } from "../components/links/IdLink";
import { formatLogMessage } from "../utils/formatting";

export function PageLogDetail() {
  const navigate = useNavigate();
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { logId } = useParams<{ logId: string }>();

  const { log, loading, error } = useLog({
    serviceSlug,
    logId,
    enabled: !!serviceSlug && !!logId,
  });

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product’s services list.
        </Alert>
      </Box>
    );
  }

  if (!loading && !log) {
    return (
      <Box>
        <PageErrorState message="Log not found." />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel
        title="Log detail"
        subtitle={
          log
            ? `${log.service_name}${log.service_instance ? ` (${log.service_instance})` : ""}`
            : undefined
        }
        toolbar={
          <Typography
            variant="caption"
            sx={{ opacity: 0.75, cursor: "pointer" }}
            onClick={() => navigate(-1)}
          >
            Back
          </Typography>
        }
      >
        {error ? <PageErrorState message={error} sx={{ mt: 1.5 }} /> : null}
        {loading ? <PageLoadingState message="Loading log…" sx={{ mt: 1.5 }} /> : null}
      </Panel>

      {log ? (
        <>
          <Panel title="Overview">
            <Stack spacing={1}>
              <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", alignItems: "center" }}>
                <Typography variant="body2">
                  <b>Log ID:</b> <span className="om-text-mono">{log.log_id}</span>
                </Typography>

                <Chip
                  size="small"
                  label={`${log.severity_text || "UNKNOWN"} (${log.severity_number})`}
                  color={getSeverityChipColor(log.severity_number)}
                  variant="outlined"
                />
              </Stack>

              <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", opacity: 0.85 }}>
                <Typography variant="body2">
                  <b>Time:</b> {new Date(log.ts).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  <b>Observed:</b>{" "}
                  {log.observed_ts ? new Date(log.observed_ts).toLocaleString() : "-"}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
                <Typography variant="body2">
                  <b>Trace ID:</b>{" "}
                  <TraceIdLink serviceSlug={serviceSlug} traceId={log.trace_id} truncateAt={0} />
                </Typography>

                <Typography variant="body2">
                  <b>Span ID:</b>{" "}
                  <SpanIdLink serviceSlug={serviceSlug} spanId={log.span_id} truncateAt={0} />
                </Typography>
              </Stack>
            </Stack>
          </Panel>

          <Panel title="Message">
            <Typography
              variant="body2"
              className="om-text-mono"
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {formatLogMessage(log.body, log.log_attrs)}
            </Typography>
          </Panel>

          <Panel title="Log attributes">
            <JsonTree value={log.log_attrs} />
          </Panel>

          <Panel title="Resource attributes">
            <JsonTree value={log.resource_attrs} />
          </Panel>
        </>
      ) : null}
    </Box>
  );
}
